import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import BookConsultationButton from "@/components/BookConsultationButton";
import ReviewProofStrip from "@/components/ReviewProofStrip";
import CaseStudyProofLinks from "@/components/CaseStudyProofLinks";
import { 
  Home, MapPin, Star, TrendingUp, Users, Target, 
  CheckCircle, ArrowRight, Phone, Calendar, Building2,
  Camera, Share2, BarChart3, MessageSquare, Download, FileText
} from "lucide-react";

const RealEstateMarketingPage = () => {

  useEffect(() => {
    document.title = "Real Estate Marketing Dallas | Realtor SEO | Digital Media Ninja";
  }, []);

  const stats = [
    { value: "Local SEO", label: "Neighborhood Visibility", icon: TrendingUp },
    { value: "Listings", label: "Listing Promotion", icon: Home },
    { value: "Content", label: "Market Reports & Video", icon: Target },
    { value: "Tracking", label: "Lead-Source Attribution", icon: BarChart3 }
  ];

  const services = [
    { icon: MapPin, title: "Neighborhood SEO", desc: "Rank #1 for 'homes for sale in [neighborhood]' and hyperlocal Dallas searches." },
    { icon: Camera, title: "Listing Marketing", desc: "Professional listing promotion with virtual tours, video, and social media distribution." },
    { icon: Share2, title: "Social Media Marketing", desc: "Build your personal brand on Instagram, Facebook, TikTok, and YouTube." },
    { icon: Building2, title: "IDX Website Optimization", desc: "Convert more visitors with optimized IDX integration and lead capture." },
    { icon: MessageSquare, title: "Lead Nurturing", desc: "Automated email campaigns that turn prospects into buyers and sellers." },
    { icon: BarChart3, title: "ROI Tracking", desc: "Track every lead from first click to closed deal with full attribution." }
  ];

  const neighborhoods = [
    "Highland Park", "University Park", "Preston Hollow", "Lakewood",
    "Uptown Dallas", "Oak Lawn", "Bishop Arts", "Deep Ellum",
    "Kessler Park", "Turtle Creek", "Knox-Henderson", "Greenville Ave",
    "Southlake", "Frisco", "Plano", "McKinney"
  ];

  const clientTypes = [
    "Individual Agents", "Team Leaders", "Brokerages", "Property Managers",
    "Luxury Specialists", "New Construction", "Commercial Brokers", "Investor Agents"
  ];

  const faqs = [
    { 
      q: "How do I get more real estate leads online?", 
      a: "Real estate leads come from multiple channels: Local SEO (ranking for neighborhood searches like 'homes for sale in Highland Park'), targeted Facebook and Google Ads, email marketing to your sphere, and building authority through market update content. We create a comprehensive lead generation strategy that brings in both buyer and seller leads consistently." 
    },
    { 
      q: "Should I focus on buyers or sellers?", 
      a: "We recommend a balanced approach, but seller leads typically have higher value since listings generate buyer leads. We create specific campaigns for each: social media and buyer-focused content for buyer leads, home valuation tools and neighborhood market reports for seller leads. Many agents see 3-5x ROI focusing more on seller acquisition." 
    },
    { 
      q: "How important is social media for real estate?", 
      a: "Extremely important. Real estate is visual and personal—people want to work with agents they know and trust. Instagram, Facebook, and increasingly TikTok are powerful for showcasing listings, sharing market insights, and building your personal brand. Agents who post consistently see 2-3x more inbound leads than those who don't." 
    },
    { 
      q: "What's the best way to market luxury listings?", 
      a: "Luxury listings require elevated marketing: professional photography and video, drone footage, virtual tours, targeted social media ads to affluent audiences, luxury real estate portal syndication, and PR/press placement. We also leverage Google Ads targeting high-net-worth keywords and retargeting campaigns to stay top-of-mind." 
    }
  ];

  const caseStudyResults = [
    { metric: "Monthly Leads", before: "18", after: "65", change: "+261%" },
    { metric: "Listing Views", before: "2,400", after: "8,500", change: "+254%" },
    { metric: "Closed Volume", before: "$4.2M", after: "$9.8M", change: "+133%" }
  ];

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "name": "Real Estate Marketing & Realtor SEO Services Dallas",
        "provider": {
          "@type": "LocalBusiness",
          "name": "Digital Media Ninja",
          "address": { "@type": "PostalAddress", "addressLocality": "Dallas", "addressRegion": "TX", "addressCountry": "US" },
          "telephone": "(432) 934-9470"
        },
        "areaServed": neighborhoods.slice(0, 8).map(n => ({ "@type": "Place", "name": n })),
        "description": "Digital marketing for real estate agents, brokers, and property managers in Dallas-Fort Worth. Realtor SEO, social media marketing, and lead generation.",
        "serviceType": "Real Estate Marketing Services"
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": { "@type": "Answer", "text": faq.a }
        }))
      }
    ]
  };

  return (
    <div className="min-h-screen bg-black" data-testid="real-estate-marketing-page">
      <Helmet>
        <title>Real Estate Marketing Dallas | Realtor SEO | Digital Media Ninja</title>
        <meta name="description" content="Expert real estate marketing and SEO services for Dallas agents and brokers. Generate more buyer and seller leads with neighborhood SEO, social media marketing, and listing promotion." />
        <meta name="keywords" content="real estate marketing Dallas, realtor SEO, agent marketing DFW, real estate lead generation, realtor advertising Dallas, luxury real estate marketing, real estate social media" />
        <link rel="canonical" href="https://digitalmedianinja.com/real-estate-marketing" />
        <meta property="og:title" content="Real Estate Marketing Dallas | Realtor SEO" />
        <meta property="og:description" content="Expert real estate marketing for Dallas agents. SEO, social media, listing marketing." />
        <meta property="og:url" content="https://digitalmedianinja.com/real-estate-marketing" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative pt-32 pb-20">
        <div className="absolute inset-0 bg-gradient-to-br from-violet-600/10 via-transparent to-purple-500/10"></div>
        <div className="container-custom relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-500/10 border border-violet-500/30 rounded-full mb-6">
              <Home className="w-4 h-4 text-violet-400" />
              <span className="text-violet-400 text-sm font-medium">Real Estate Marketing Specialists</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Generate More <span className="text-violet-400">Real Estate Leads</span> in Dallas
            </h1>
            
            <p className="text-lg text-gray-300 mb-8 max-w-2xl">
              Stand out in the competitive DFW real estate market. We help agents and brokers generate more buyer and seller leads with strategic digital marketing.
            </p>
            
            <div className="flex flex-wrap gap-4 mb-12">
              <BookConsultationButton meetingType="realEstate" size="lg">
                Get Free Real Estate Marketing Audit <ArrowRight className="w-5 h-5 ml-2" />
              </BookConsultationButton>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-violet-400/30 text-violet-400 hover:bg-violet-400/10"
                onClick={() => window.location.href='/blog/how-to-market-a-real-estate-business'}
                data-testid="read-blog-btn"
              >
                <FileText className="w-5 h-5 mr-2" /> Read Our Full Guide
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white/20 text-white hover:bg-white/10"
                onClick={() => window.location.href='/guides/real-estate-marketing'}
                data-testid="read-playbook-btn"
              >
                <Download className="w-5 h-5 mr-2" /> Marketing Playbook
              </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {stats.map((stat, i) => (
                <div key={i} className="text-center p-4 bg-white/5 rounded-xl border border-white/10">
                  <stat.icon className="w-6 h-6 text-violet-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{stat.value}</p>
                  <p className="text-xs text-gray-400">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-white text-center mb-4">Real Estate Marketing Services</h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">Comprehensive digital marketing designed to help you close more deals.</p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((svc, i) => (
              <Card key={i} className="bg-[#111] border-white/10 hover:border-violet-400/30 transition-all">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-violet-400/10 flex items-center justify-center mb-4">
                    <svc.icon className="w-6 h-6 text-violet-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{svc.title}</h3>
                  <p className="text-gray-400 text-sm">{svc.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Neighborhoods */}
      <section className="py-20 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-white text-center mb-4">Dallas Neighborhoods We Market</h2>
          <p className="text-gray-400 text-center mb-12">Dominate search for the most sought-after Dallas neighborhoods.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
            {neighborhoods.map((hood, i) => (
              <div key={i} className="p-3 bg-white/5 rounded-xl border border-white/10 text-center hover:border-violet-400/30 transition-colors">
                <p className="text-white font-medium text-sm">{hood}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Types */}
      <section className="py-20">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Who We Help</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {clientTypes.map((type, i) => (
              <div key={i} className="p-4 bg-white/5 rounded-xl border border-white/10 text-center hover:border-violet-400/30 transition-colors">
                <p className="text-white font-medium">{type}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study Results */}
      <section className="py-20 bg-gradient-to-b from-[#0a0a0a] to-black">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-white text-center mb-4">Real Results for Real Estate Agents</h2>
          <p className="text-gray-400 text-center mb-12">See how we helped a Dallas luxury agent double their business.</p>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {caseStudyResults.map((result, i) => (
              <div key={i} className="text-center p-8 bg-[#111] rounded-2xl border border-white/10">
                <p className="text-gray-400 mb-2">{result.metric}</p>
                <div className="flex items-center justify-center gap-4 mb-2">
                  <span className="text-gray-500 line-through">{result.before}</span>
                  <ArrowRight className="w-4 h-4 text-violet-400" />
                  <span className="text-2xl font-bold text-white">{result.after}</span>
                </div>
                <p className="text-violet-400 font-semibold">{result.change}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CaseStudyProofLinks slugs={["dallasluxuryrealty", "dfwapartmentlocators"]} heading="Real estate sites we've built visibility for" />

      <ReviewProofStrip />

      {/* FAQ */}
      <section className="py-20">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Real Estate Marketing FAQs</h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, i) => (
              <details key={i} className="group bg-[#111] rounded-xl border border-white/10">
                <summary className="p-6 cursor-pointer text-white font-medium flex items-center justify-between hover:bg-white/5">
                  {faq.q}
                  <span className="text-violet-400 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="px-6 pb-6 text-gray-400">{faq.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-violet-500/10 to-purple-500/10">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Ready to Grow Your Real Estate Business?</h2>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">Get a free real estate marketing audit and custom strategy to generate more leads.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <BookConsultationButton meetingType="realEstate" size="lg">
              <Calendar className="w-5 h-5 mr-2" /> Book Strategy Call
            </BookConsultationButton>
            <a href="tel:+14329349470">
              <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10">
                Call (432) 934-9470
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RealEstateMarketingPage;
